import { FunctionComponent, useContext } from 'react'
import styled, { ThemeContext } from 'styled-components'

import DisclaimerText from 'components/DisclaimerText'
import ErrorBox from 'components/ErrorBox'
import Header from 'components/GlobalStyles/Header'
import RoundedButton from 'components/RoundedButton'
import Spacer from 'components/Spacer'

import { addCommas } from 'utils/stringFormatting'

import * as T from 'types'
import * as E from 'types/enums'

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  row-gap: 30px;

  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    display: grid;
    grid-template-columns: minmax(120px, 345px) minmax(120px, 345px) minmax(120px, 345px);
    grid-template-rows: 1fr;
    column-gap: 20px;
  }
  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    column-gap: 30px;
  }
`

const PricingPackageContainer = styled.div``

const PackageInfoContainer = styled.div`
  width: 100%;
  padding: 0 20px;
  text-align: center;
  background-color: white;
  border: 1px solid ${props => props.theme.colors.grey7};
  border-top: none;
  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    flex-direction: row;
  }
`

interface IColorProps {
  color: string
}
const PackageName = styled.div<IColorProps>`
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 24px;
  height: 60px;
  color: white;
  background-color: ${props => props.color};
  border: 1px solid ${props => props.color};
  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    font-size: 48px;
    height: 100px;
  }
`

const PackageInfo = styled.div`
  font-size: 18px;
  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    font-size: 22px;
  }
`

const Price = styled.div`
  font-size: 24px;
  font-weight: 900;
  margin: 20px 0;

  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    font-size: 48px;
    margin: 32px 0;
  }
`

const PricingInfo = styled.div`
  font-size: 20px;
  margin: 5px 0;

  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    margin: 10px 0;
    font-size: 30px;
  }
`

const Highlight = styled.div`
  margin: 0 auto;
  width: 125px;
  height: 10px;
  background-color: ${props => props.theme.colors.yellow};
  margin-top: -14px;
  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    width: 135px;
  }
  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    margin-top: -20px;
    width: 185px;
  }
`

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  margin: 25px 0;
  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    margin: 35px 0;
  }
`

const MaxWidthContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 275px;
  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    width: 375px;
  }
`

interface IProps {
  pricingPackages: T.IPricingPackage[] | undefined
  selectedState: T.IState | undefined
}

const PricingPackages: FunctionComponent<IProps> = ({ pricingPackages, selectedState }) => {
  const theme = useContext(ThemeContext)

  const errorMessage = 'We were unable to load packages at this time.'
  if (!pricingPackages?.length) return <ErrorBox error={errorMessage} />

  const basicPackage = pricingPackages.find(pkg => pkg.name === E.PackageType.basic)
  const premiumPackage = pricingPackages.find(pkg => pkg.name === E.PackageType.premium)
  const platinumPackage = pricingPackages.find(pkg => pkg.name === E.PackageType.platinum)

  if (!basicPackage || !premiumPackage || !platinumPackage) return <ErrorBox error={errorMessage} />

  const showDisclaimer = basicPackage.payNow === 0

  return (
    <>
      <Header
        text="Pricing Packages for Everyone"
        fontSize={24}
        fontTabletSize={32}
        fontDesktopSize={42}
        headerThree
      />
      <Spacer
        size={50}
        tabletSize={30}
        responsiveSize={20}
      />
      <GridContainer>
        <PricingPackageContainer>
          <PackageName color={theme.colors.blue}>{basicPackage.name}</PackageName>
          <PackageInfoContainer>
            <Spacer size={20} />
            <PackageInfo>Pay only when you sell</PackageInfo>
            <Price>{`$${addCommas(basicPackage.price)}`}</Price>
            <PricingInfo>
              {showDisclaimer
                ? `${basicPackage.blurb.toUpperCase()}*`
                : `$${addCommas(basicPackage.payNow)} Today`}
            </PricingInfo>
            <Highlight />
            <PricingInfo>{`$${addCommas(basicPackage.payAtClose)} at Closing`}</PricingInfo>
            <ButtonContainer>
              <RoundedButton
                href="/listing/create"
                text="Sign up"
                background={theme.colors.blue4}
                hoverBackground={theme.colors.blue9}
                color={theme.colors.white}
                fontSize={16}
                radius={50}
                height={50}
                fullWidth
              />
            </ButtonContainer>
          </PackageInfoContainer>
          {showDisclaimer && <DisclaimerText>*Availability varies by location</DisclaimerText>}
        </PricingPackageContainer>
        <PricingPackageContainer>
          <PackageName color={theme.colors.blue6}>{premiumPackage.name}</PackageName>
          <PackageInfoContainer>
            <Spacer size={20} />
            <PackageInfo>Tools to sell like a pro</PackageInfo>
            <Price>{`$${addCommas(premiumPackage.price)}`}</Price>
            <PricingInfo>{`$${addCommas(premiumPackage.payNow)} Today`}</PricingInfo>
            <PricingInfo>{`$${addCommas(premiumPackage.payAtClose)} at Closing`}</PricingInfo>
            <ButtonContainer>
              <RoundedButton
                href="/listing/create"
                text="Sign up"
                background={theme.colors.blue4}
                hoverBackground={theme.colors.blue9}
                color={theme.colors.white}
                fontSize={16}
                radius={50}
                height={50}
                fullWidth
              />
            </ButtonContainer>
          </PackageInfoContainer>
        </PricingPackageContainer>
        <PricingPackageContainer>
          <PackageName color={theme.colors.blue8}>{platinumPackage.name}</PackageName>
          <PackageInfoContainer>
            <Spacer
              size={20}
              responsiveSize={25}
            />
            <PackageInfo>The complete package</PackageInfo>
            <Price>{`$${addCommas(platinumPackage.price)}`}</Price>
            <PricingInfo>{`$${addCommas(platinumPackage.payNow)} Today`}</PricingInfo>
            <PricingInfo>{`$${addCommas(platinumPackage.payAtClose)} at Closing`}</PricingInfo>
            <ButtonContainer>
              <RoundedButton
                href="/listing/create"
                text="Sign up"
                background={theme.colors.blue4}
                hoverBackground={theme.colors.blue9}
                color={theme.colors.white}
                fontSize={16}
                radius={50}
                height={50}
                fullWidth
              />
            </ButtonContainer>
          </PackageInfoContainer>
        </PricingPackageContainer>
      </GridContainer>
      <Spacer size={30} />
      <MaxWidthContainer>
        <RoundedButton
          href={selectedState ? `/pricing?state=${selectedState.abbreviation}` : '/pricing'}
          text="Learn More"
          background={theme.colors.blue4}
          hoverBackground={theme.colors.blue9}
          color={theme.colors.white}
          fontSize={16}
          radius={50}
          height={50}
          fullWidth
        />
      </MaxWidthContainer>
    </>
  )
}

export default PricingPackages
